import React, { useState, useRef } from 'react';
import { Printer, X, FileText, Truck, Anchor, Package, FileCheck } from 'lucide-react';

/* ══════════════════════════════════════════════════════════════════
   Helpers
══════════════════════════════════════════════════════════════════ */
const fmt2 = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
};

/* ── Shared print CSS injected once ── */
const PRINT_CSS = `
@media print {
    body * { visibility: hidden !important; }
    #print-area, #print-area * { visibility: visible !important; }
    #print-area { position: fixed; inset: 0; padding: 0; margin: 0; }
    @page { margin: 10mm; size: A4; }
}
.print-doc {
    font-family: 'Segoe UI', Arial, sans-serif;
    font-size: 12px;
    color: #1a1a1a;
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
    background: #fff;
}
.print-doc table { width: 100%; border-collapse: collapse; }
.print-doc th, .print-doc td { border: 1px solid #ccc; padding: 6px 10px; }
.print-doc th { background: #f0f0f0; font-weight: 600; text-align: left; }
.print-doc .doc-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; border-bottom: 2px solid #2c3e50; padding-bottom: 12px; }
.print-doc .company-name { font-size: 18px; font-weight: 700; color: #2c3e50; }
.print-doc .doc-title { font-size: 16px; font-weight: 700; text-align: right; color: #2c3e50; }
.print-doc .doc-ref { font-size: 11px; color: #555; }
.print-doc .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #ccc; margin-bottom: 12px; }
.print-doc .info-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; border: 1px solid #ccc; margin-bottom: 12px; }
.print-doc .info-cell { padding: 6px 10px; border-right: 1px solid #ccc; border-bottom: 1px solid #ccc; }
.print-doc .info-cell:nth-child(even) { border-right: none; }
.print-doc .info-cell label { font-size: 10px; font-weight: 600; color: #666; text-transform: uppercase; display: block; }
.print-doc .info-cell span { font-size: 12px; font-weight: 500; }
.print-doc .section-title { font-size: 13px; font-weight: 700; color: #2c3e50; margin: 14px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #ddd; }
.print-doc .sig-row { display: flex; justify-content: space-between; margin-top: 40px; }
.print-doc .sig-box { text-align: center; width: 200px; border-top: 1px solid #333; padding-top: 6px; font-size: 11px; }
.print-doc .total-row td { font-weight: 700; background: #f8f8f8; }
.print-doc .right { text-align: right; }
.print-doc .center { text-align: center; }
.print-doc .watermark { color: #aaa; font-size: 11px; text-align: center; margin-top: 20px; border-top: 1px solid #eee; padding-top: 8px; }
`;

/* ══════════════════════════════════════════════════════════════════
   Document renderers
══════════════════════════════════════════════════════════════════ */

/* 1. INVOICE */
const InvoiceDoc = ({ formData, masterData, companyName }) => {
    const inv = formData.invoiceDetails || [];
    const totals = inv.reduce((a, r) => ({
        prov: a.prov + (parseFloat(r.ProvisionHome) || 0),
        sales: a.sales + (parseFloat(r.SalesHome) || 0),
        tax: a.tax + (parseFloat(r.TaxAmt) || 0),
    }), { prov: 0, sales: 0, tax: 0 });

    const customer = (masterData.customers || []).find(c => c.id === formData.InvoiceToID);
    const loadPort = (masterData.ports || []).find(p => p.id === formData.LoadPortID);
    const destPort = (masterData.ports || []).find(p => p.id === formData.DestinationPortID);
    const vessel = (masterData.vessels || []).find(v => v.id === formData.VesselID);

    return (
        <div className="print-doc">
            <div className="doc-header">
                <div>
                    <div className="company-name">{companyName}</div>
                    <div className="doc-ref">Freight & Logistics Services</div>
                </div>
                <div className="right">
                    <div className="doc-title">INVOICE</div>
                    <div className="doc-ref">Job No: {formData.JobCode}</div>
                    <div className="doc-ref">Date: {fmtDate(formData.JobDate)}</div>
                    {formData.InvoiceNo && <div className="doc-ref">Invoice No: {formData.InvoiceNo}</div>}
                </div>
            </div>

            <div className="info-grid">
                <div className="info-cell"><label>Invoice To</label><span>{customer?.name || '—'}</span></div>
                <div className="info-cell"><label>Invoice Date</label><span>{fmtDate(formData.JobDate)}</span></div>
                <div className="info-cell"><label>Consigner</label><span>{(masterData.customers || []).find(c => c.id === formData.ConsignerID)?.name || '—'}</span></div>
                <div className="info-cell"><label>Consignee</label><span>{(masterData.customers || []).find(c => c.id === formData.ConsigneeID)?.name || '—'}</span></div>
                <div className="info-cell"><label>Load Port</label><span>{loadPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Destination Port</label><span>{destPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Vessel / Voyage</label><span>{vessel?.name || '—'} {formData.VoyageNo ? `/ ${formData.VoyageNo}` : ''}</span></div>
                <div className="info-cell"><label>B/L No</label><span>{formData.HBL || formData.MBL || '—'}</span></div>
                <div className="info-cell"><label>Sailing Date</label><span>{fmtDate(formData.SailingDate)}</span></div>
                <div className="info-cell"><label>Arrival Date</label><span>{fmtDate(formData.ArrivalDate)}</span></div>
                <div className="info-cell"><label>Gross Weight</label><span>{formData.GrossWeight || '—'} kg</span></div>
                <div className="info-cell"><label>Packages</label><span>{formData.Package || '—'}</span></div>
            </div>

            <div className="section-title">Charges</div>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th className="right">Provision (₹)</th>
                        <th className="right">Sales (₹)</th>
                        <th className="right">Tax (%)</th>
                        <th className="right">Tax Amt (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {inv.length === 0
                        ? <tr><td colSpan="6" className="center" style={{ color: '#999', padding: 16 }}>No charges added</td></tr>
                        : inv.map((r, i) => {
                            const rt = (masterData.revenueTypes || []).find(x => x.id === r.RevenueTypeID);
                            return (
                                <tr key={i}>
                                    <td className="center">{i + 1}</td>
                                    <td>{rt?.name || r.RevenueCode || '—'}</td>
                                    <td className="right">{fmt2(r.ProvisionHome)}</td>
                                    <td className="right">{fmt2(r.SalesHome)}</td>
                                    <td className="center">{r.TaxPer || 0}%</td>
                                    <td className="right">{fmt2(r.TaxAmt)}</td>
                                </tr>
                            );
                        })}
                    <tr className="total-row">
                        <td colSpan="2" className="right">TOTAL</td>
                        <td className="right">{fmt2(totals.prov)}</td>
                        <td className="right">{fmt2(totals.sales)}</td>
                        <td></td>
                        <td className="right">{fmt2(totals.tax)}</td>
                    </tr>
                    <tr className="total-row">
                        <td colSpan="5" className="right">GRAND TOTAL (Sales + Tax)</td>
                        <td className="right">₹ {fmt2(totals.sales + totals.tax)}</td>
                    </tr>
                </tbody>
            </table>

            {formData.Remarks && <><div className="section-title">Remarks</div><p>{formData.Remarks}</p></>}

            <div className="sig-row">
                <div className="sig-box">Prepared By</div>
                <div className="sig-box">Authorised Signatory</div>
            </div>
            <div className="watermark">This is a computer-generated document — {formData.JobCode}</div>
        </div>
    );
};

/* 2. TRUCK CONSIGNMENT NOTE */
const TruckConsignmentDoc = ({ formData, masterData, companyName }) => {
    const transporter = (masterData.transporters || []).find(t => t.id === formData.TransporterID);
    const carrier = (masterData.carriers || []).find(c => c.id === formData.CarrierID);
    const consigner = (masterData.customers || []).find(c => c.id === formData.ConsignerID);
    const consignee = (masterData.customers || []).find(c => c.id === formData.ConsigneeID);
    const cargo = formData.cargoDescriptions || [];
    const containers = formData.containerDetails || [];

    const totalWeight = cargo.reduce((s, r) => s + (parseFloat(r.GrossWeight) || 0), 0);
    const totalPkgs = cargo.reduce((s, r) => s + (parseFloat(r.Packages) || 0), 0);

    return (
        <div className="print-doc">
            <div className="doc-header">
                <div>
                    <div className="company-name">{companyName}</div>
                    <div className="doc-ref">Freight & Logistics Services</div>
                </div>
                <div className="right">
                    <div className="doc-title">TRUCK CONSIGNMENT NOTE</div>
                    <div className="doc-ref">Job No: {formData.JobCode}</div>
                    <div className="doc-ref">Date: {fmtDate(formData.JobDate)}</div>
                </div>
            </div>

            <div className="section-title">Consignment Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Consigner (Sender)</label><span>{consigner?.name || '—'}</span></div>
                <div className="info-cell"><label>Consignee (Receiver)</label><span>{consignee?.name || '—'}</span></div>
                <div className="info-cell"><label>Collection Point</label><span>{formData.CollectionPoint || '—'}</span></div>
                <div className="info-cell"><label>Delivery Point</label><span>{formData.DeliveryPoint || '—'}</span></div>
                <div className="info-cell"><label>Collection Date</label><span>{fmtDate(formData.CollectionDate)}</span></div>
                <div className="info-cell"><label>Delivery Date</label><span>{fmtDate(formData.DeliveryDate)}</span></div>
                <div className="info-cell"><label>Delivery Note No</label><span>{formData.DeliveryNote || '—'}</span></div>
                <div className="info-cell"><label>Delivery Order No</label><span>{formData.DeliveryOrderNo || '—'}</span></div>
            </div>

            <div className="section-title">Transport Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Transporter</label><span>{transporter?.name || '—'}</span></div>
                <div className="info-cell"><label>Carrier</label><span>{carrier?.name || '—'}</span></div>
                <div className="info-cell"><label>Truck Reg No</label><span>{formData.TruckRegNo || '—'}</span></div>
                <div className="info-cell"><label>Driver Details</label><span>{formData.DriverDetails || '—'}</span></div>
            </div>

            <div className="section-title">Cargo Details</div>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Mark</th>
                        <th>Description</th>
                        <th className="right">Packages</th>
                        <th className="right">Weight (kg)</th>
                        <th className="right">Gross Weight (kg)</th>
                        <th className="right">Volume (CBM)</th>
                    </tr>
                </thead>
                <tbody>
                    {cargo.length === 0
                        ? <tr><td colSpan="7" className="center" style={{ color: '#999', padding: 16 }}>No cargo</td></tr>
                        : cargo.map((r, i) => (
                            <tr key={i}>
                                <td className="center">{i + 1}</td>
                                <td>{r.Mark || '—'}</td>
                                <td>{r.Description || '—'}</td>
                                <td className="right">{r.Packages || 0}</td>
                                <td className="right">{fmt2(r.Weight)}</td>
                                <td className="right">{fmt2(r.GrossWeight)}</td>
                                <td className="right">{fmt2(r.Volume)}</td>
                            </tr>
                        ))}
                    <tr className="total-row">
                        <td colSpan="3" className="right">TOTAL</td>
                        <td className="right">{totalPkgs}</td>
                        <td></td>
                        <td className="right">{fmt2(totalWeight)}</td>
                        <td></td>
                    </tr>
                </tbody>
            </table>

            {containers.length > 0 && <>
                <div className="section-title">Container Details</div>
                <table>
                    <thead>
                        <tr><th>#</th><th>Container No</th><th>Seal No</th><th>Description</th></tr>
                    </thead>
                    <tbody>
                        {containers.map((c, i) => (
                            <tr key={i}><td className="center">{i + 1}</td><td>{c.ContainerNo || '—'}</td><td>{c.SealNo || '—'}</td><td>{c.Description || '—'}</td></tr>
                        ))}
                    </tbody>
                </table>
            </>}

            {formData.CollectionInstructions && <><div className="section-title">Collection Instructions</div><p>{formData.CollectionInstructions}</p></>}
            {formData.DeliveryInstructions && <><div className="section-title">Delivery Instructions</div><p>{formData.DeliveryInstructions}</p></>}

            <div className="sig-row">
                <div className="sig-box">Sender's Signature</div>
                <div className="sig-box">Driver's Signature</div>
                <div className="sig-box">Receiver's Signature</div>
            </div>
            <div className="watermark">Truck Consignment Note — {formData.JobCode}</div>
        </div>
    );
};

/* 3. ARRIVAL NOTE */
const ArrivalNoteDoc = ({ formData, masterData, companyName }) => {
    const vessel = (masterData.vessels || []).find(v => v.id === formData.VesselID);
    const airline = (masterData.airlines || []).find(a => a.id === formData.AirlineID);
    const loadPort = (masterData.ports || []).find(p => p.id === formData.LoadPortID);
    const destPort = (masterData.ports || []).find(p => p.id === formData.DestinationPortID);
    const consignee = (masterData.customers || []).find(c => c.id === formData.ConsigneeID);
    const shipper = (masterData.customers || []).find(c => c.id === formData.ShipperID);
    const cargo = formData.cargoDescriptions || [];
    const containers = formData.containerDetails || [];

    const isAir = !!(formData.AirlineID || formData.MAWB || formData.HAWB);

    return (
        <div className="print-doc">
            <div className="doc-header">
                <div>
                    <div className="company-name">{companyName}</div>
                    <div className="doc-ref">Freight & Logistics Services</div>
                </div>
                <div className="right">
                    <div className="doc-title">ARRIVAL NOTICE</div>
                    <div className="doc-ref">Job No: {formData.JobCode}</div>
                    <div className="doc-ref">Date: {fmtDate(formData.ArrivalDate)}</div>
                </div>
            </div>

            <div className="section-title">Shipment Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Consignee</label><span>{consignee?.name || '—'}</span></div>
                <div className="info-cell"><label>Shipper</label><span>{shipper?.name || '—'}</span></div>
                <div className="info-cell"><label>Port of Loading</label><span>{loadPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Port of Destination</label><span>{destPort?.name || '—'}</span></div>
                {isAir ? (
                    <>
                        <div className="info-cell"><label>Airline</label><span>{airline?.name || '—'}</span></div>
                        <div className="info-cell"><label>Flight No</label><span>{formData.Flight || '—'}</span></div>
                        <div className="info-cell"><label>MAWB No</label><span>{formData.MAWB || '—'}</span></div>
                        <div className="info-cell"><label>HAWB No</label><span>{formData.HAWB || '—'}</span></div>
                    </>
                ) : (
                    <>
                        <div className="info-cell"><label>Vessel</label><span>{vessel?.name || '—'}</span></div>
                        <div className="info-cell"><label>Voyage No</label><span>{formData.VoyageNo || '—'}</span></div>
                        <div className="info-cell"><label>Master B/L</label><span>{formData.MBL || '—'}</span></div>
                        <div className="info-cell"><label>House B/L</label><span>{formData.HBL || '—'}</span></div>
                    </>
                )}
                <div className="info-cell"><label>B/L Date</label><span>{fmtDate(formData.BLDate)}</span></div>
                <div className="info-cell"><label>Arrival Date</label><span>{fmtDate(formData.ArrivalDate)}</span></div>
                <div className="info-cell"><label>Gross Weight</label><span>{formData.GrossWeight ? `${formData.GrossWeight} kg` : '—'}</span></div>
                <div className="info-cell"><label>Volume</label><span>{formData.Volume ? `${formData.Volume} CBM` : '—'}</span></div>
                <div className="info-cell"><label>Packages</label><span>{formData.Package || '—'}</span></div>
                <div className="info-cell"><label>Rotation No</label><span>{formData.RotationNo || '—'}</span></div>
                <div className="info-cell"><label>MRN</label><span>{formData.MRN || '—'}</span></div>
                <div className="info-cell"><label>Delivery Order No</label><span>{formData.DeliveryOrderNo || '—'}</span></div>
            </div>

            <div className="section-title">Cargo Description</div>
            <table>
                <thead>
                    <tr><th>#</th><th>Mark</th><th>Description</th><th className="right">Packages</th><th className="right">Weight (kg)</th><th className="right">Volume (CBM)</th></tr>
                </thead>
                <tbody>
                    {cargo.length === 0
                        ? <tr><td colSpan="6" className="center" style={{ color: '#999', padding: 16 }}>No cargo details</td></tr>
                        : cargo.map((r, i) => (
                            <tr key={i}>
                                <td className="center">{i + 1}</td>
                                <td>{r.Mark || '—'}</td>
                                <td>{r.Description || '—'}</td>
                                <td className="right">{r.Packages || 0}</td>
                                <td className="right">{fmt2(r.Weight)}</td>
                                <td className="right">{fmt2(r.Volume)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>

            {containers.length > 0 && <>
                <div className="section-title">Container Details</div>
                <table>
                    <thead>
                        <tr><th>#</th><th>Container No</th><th>Seal No</th><th>Description</th></tr>
                    </thead>
                    <tbody>
                        {containers.map((c, i) => (
                            <tr key={i}><td className="center">{i + 1}</td><td>{c.ContainerNo || '—'}</td><td>{c.SealNo || '—'}</td><td>{c.Description || '—'}</td></tr>
                        ))}
                    </tbody>
                </table>
            </>}

            <div style={{ marginTop: 16, padding: '10px 14px', background: '#fffbf0', border: '1px solid #e6c96e', borderRadius: 6, fontSize: 12 }}>
                <strong>Note:</strong> Kindly arrange to take delivery of the above shipment at your earliest. All charges are due and payable before delivery.
            </div>

            {formData.ShippingInstruction && <><div className="section-title">Shipping Instructions</div><p>{formData.ShippingInstruction}</p></>}

            <div className="sig-row">
                <div className="sig-box">Agent Signature</div>
                <div className="sig-box">Authorised Signatory</div>
            </div>
            <div className="watermark">Arrival Notice — {formData.JobCode}</div>
        </div>
    );
};

/* 4. CUSTOMS ENTRY */
const CustomsEntryDoc = ({ formData, masterData, companyName }) => {
    const consigner = (masterData.customers || []).find(c => c.id === formData.ConsignerID);
    const consignee = (masterData.customers || []).find(c => c.id === formData.ConsigneeID);
    const loadPort = (masterData.ports || []).find(p => p.id === formData.LoadPortID);
    const destPort = (masterData.ports || []).find(p => p.id === formData.DestinationPortID);
    const country = (masterData.countries || []).find(c => c.id === formData.CountryofOriginID);
    const vessel = (masterData.vessels || []).find(v => v.id === formData.VesselID);
    const cargo = formData.cargoDescriptions || [];
    const billOfEntry = formData.billOfEntry || [];
    const inv = formData.invoiceDetails || [];

    const totalSales = inv.reduce((s, r) => s + (parseFloat(r.SalesHome) || 0), 0);
    const totalTax = inv.reduce((s, r) => s + (parseFloat(r.TaxAmt) || 0), 0);

    return (
        <div className="print-doc">
            <div className="doc-header">
                <div>
                    <div className="company-name">{companyName}</div>
                    <div className="doc-ref">Customs Broker / Freight Forwarder</div>
                </div>
                <div className="right">
                    <div className="doc-title">CUSTOMS ENTRY</div>
                    <div className="doc-ref">Job No: {formData.JobCode}</div>
                    <div className="doc-ref">Date: {fmtDate(formData.JobDate)}</div>
                </div>
            </div>

            <div className="section-title">Entry Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Importer / Consignee</label><span>{consignee?.name || '—'}</span></div>
                <div className="info-cell"><label>Exporter / Consigner</label><span>{consigner?.name || '—'}</span></div>
                <div className="info-cell"><label>Country of Origin</label><span>{country?.name || '—'}</span></div>
                <div className="info-cell"><label>Port of Loading</label><span>{loadPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Port of Entry</label><span>{destPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Vessel / Voyage</label><span>{vessel?.name || '—'} {formData.VoyageNo ? `/ ${formData.VoyageNo}` : ''}</span></div>
                <div className="info-cell"><label>Master B/L</label><span>{formData.MBL || '—'}</span></div>
                <div className="info-cell"><label>House B/L</label><span>{formData.HBL || '—'}</span></div>
                <div className="info-cell"><label>B/L Date</label><span>{fmtDate(formData.BLDate)}</span></div>
                <div className="info-cell"><label>Arrival Date</label><span>{fmtDate(formData.ArrivalDate)}</span></div>
                <div className="info-cell"><label>Gross Weight</label><span>{formData.GrossWeight ? `${formData.GrossWeight} kg` : '—'}</span></div>
                <div className="info-cell"><label>Volume</label><span>{formData.Volume ? `${formData.Volume} CBM` : '—'}</span></div>
                <div className="info-cell"><label>Packages</label><span>{formData.Package || '—'}</span></div>
                <div className="info-cell"><label>MRN</label><span>{formData.MRN || '—'}</span></div>
                <div className="info-cell"><label>Rotation No</label><span>{formData.RotationNo || '—'}</span></div>
                <div className="info-cell"><label>IPT No</label><span>{formData.IPTNo || '—'}</span></div>
            </div>

            <div className="section-title">Bill of Entry</div>
            {billOfEntry.length > 0 ? (
                <table>
                    <thead>
                        <tr><th>#</th><th>Bill of Entry No</th><th>Date</th><th>Shipping Agent</th></tr>
                    </thead>
                    <tbody>
                        {billOfEntry.map((b, i) => (
                            <tr key={i}>
                                <td className="center">{i + 1}</td>
                                <td>{b.BillOfEntry || '—'}</td>
                                <td>{fmtDate(b.BillofEntryDate)}</td>
                                <td>{(masterData.shippingAgents || []).find(a => a.id === b.ShippingAgentID)?.name || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : <p style={{ color: '#999', fontStyle: 'italic' }}>No bill of entry records</p>}

            <div className="section-title">Cargo Description</div>
            <table>
                <thead>
                    <tr><th>#</th><th>Mark</th><th>Description</th><th className="right">Packages</th><th className="right">Weight (kg)</th><th className="right">Volume (CBM)</th></tr>
                </thead>
                <tbody>
                    {cargo.length === 0
                        ? <tr><td colSpan="6" className="center" style={{ color: '#999', padding: 16 }}>No cargo details</td></tr>
                        : cargo.map((r, i) => (
                            <tr key={i}>
                                <td className="center">{i + 1}</td>
                                <td>{r.Mark || '—'}</td>
                                <td>{r.Description || '—'}</td>
                                <td className="right">{r.Packages || 0}</td>
                                <td className="right">{fmt2(r.Weight)}</td>
                                <td className="right">{fmt2(r.Volume)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>

            <div className="section-title">Duty / Charges Summary</div>
            <table>
                <thead>
                    <tr><th>Description</th><th className="right">Taxable Amount (₹)</th><th className="right">Tax (₹)</th><th className="right">Total (₹)</th></tr>
                </thead>
                <tbody>
                    {inv.map((r, i) => {
                        const rt = (masterData.revenueTypes || []).find(x => x.id === r.RevenueTypeID);
                        return (
                            <tr key={i}>
                                <td>{rt?.name || r.RevenueCode || '—'}</td>
                                <td className="right">{fmt2(r.SalesHome)}</td>
                                <td className="right">{fmt2(r.TaxAmt)}</td>
                                <td className="right">{fmt2((parseFloat(r.SalesHome) || 0) + (parseFloat(r.TaxAmt) || 0))}</td>
                            </tr>
                        );
                    })}
                    <tr className="total-row">
                        <td className="right">TOTAL</td>
                        <td className="right">{fmt2(totalSales)}</td>
                        <td className="right">{fmt2(totalTax)}</td>
                        <td className="right">₹ {fmt2(totalSales + totalTax)}</td>
                    </tr>
                </tbody>
            </table>

            <div className="sig-row">
                <div className="sig-box">Importer's Signature</div>
                <div className="sig-box">Customs Agent</div>
                <div className="sig-box">Customs Officer</div>
            </div>
            <div className="watermark">Customs Entry — {formData.JobCode}</div>
        </div>
    );
};

/* 5. EXPORT DECLARATION */
const ExportDeclarationDoc = ({ formData, masterData, companyName }) => {
    const consigner = (masterData.customers || []).find(c => c.id === formData.ConsignerID);
    const consignee = (masterData.customers || []).find(c => c.id === formData.ConsigneeID);
    const shipper = (masterData.customers || []).find(c => c.id === formData.ShipperID);
    const loadPort = (masterData.ports || []).find(p => p.id === formData.LoadPortID);
    const destPort = (masterData.ports || []).find(p => p.id === formData.DestinationPortID);
    const country = (masterData.countries || []).find(c => c.id === formData.CountryofOriginID);
    const vessel = (masterData.vessels || []).find(v => v.id === formData.VesselID);
    const airline = (masterData.airlines || []).find(a => a.id === formData.AirlineID);
    const cargo = formData.cargoDescriptions || [];
    const containers = formData.containerDetails || [];
    const inv = formData.invoiceDetails || [];

    const totalFOB = inv.reduce((s, r) => s + (parseFloat(r.SalesHome) || 0), 0);
    const totalWeight = cargo.reduce((s, r) => s + (parseFloat(r.GrossWeight) || 0), 0);
    const totalPkgs = cargo.reduce((s, r) => s + (parseFloat(r.Packages) || 0), 0);
    const isAir = !!(formData.AirlineID);

    return (
        <div className="print-doc">
            <div className="doc-header">
                <div>
                    <div className="company-name">{companyName}</div>
                    <div className="doc-ref">Export Division</div>
                </div>
                <div className="right">
                    <div className="doc-title">EXPORT DECLARATION</div>
                    <div className="doc-ref">Job No: {formData.JobCode}</div>
                    <div className="doc-ref">Date: {fmtDate(formData.JobDate)}</div>
                    <div className="doc-ref">Ref No: {formData.RefNo || '—'}</div>
                </div>
            </div>

            <div className="section-title">Exporter / Shipper Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Exporter / Consigner</label><span>{consigner?.name || '—'}</span></div>
                <div className="info-cell"><label>Shipper</label><span>{shipper?.name || '—'}</span></div>
                <div className="info-cell"><label>Consignee</label><span>{consignee?.name || '—'}</span></div>
                <div className="info-cell"><label>Country of Origin</label><span>{country?.name || '—'}</span></div>
            </div>

            <div className="section-title">Shipment Details</div>
            <div className="info-grid">
                <div className="info-cell"><label>Port of Loading</label><span>{loadPort?.name || '—'}</span></div>
                <div className="info-cell"><label>Port of Destination</label><span>{destPort?.name || '—'}</span></div>
                {isAir ? (
                    <>
                        <div className="info-cell"><label>Airline</label><span>{airline?.name || '—'}</span></div>
                        <div className="info-cell"><label>Flight No</label><span>{formData.Flight || '—'}</span></div>
                        <div className="info-cell"><label>MAWB No</label><span>{formData.MAWB || '—'}</span></div>
                        <div className="info-cell"><label>HAWB No</label><span>{formData.HAWB || '—'}</span></div>
                    </>
                ) : (
                    <>
                        <div className="info-cell"><label>Vessel</label><span>{vessel?.name || '—'}</span></div>
                        <div className="info-cell"><label>Voyage No</label><span>{formData.VoyageNo || '—'}</span></div>
                        <div className="info-cell"><label>Master B/L</label><span>{formData.MBL || '—'}</span></div>
                        <div className="info-cell"><label>House B/L</label><span>{formData.HBL || '—'}</span></div>
                    </>
                )}
                <div className="info-cell"><label>Sailing Date</label><span>{fmtDate(formData.SailingDate)}</span></div>
                <div className="info-cell"><label>B/L Date</label><span>{fmtDate(formData.BLDate)}</span></div>
                <div className="info-cell"><label>Total Packages</label><span>{totalPkgs || formData.Package || '—'}</span></div>
                <div className="info-cell"><label>Gross Weight</label><span>{totalWeight ? `${fmt2(totalWeight)} kg` : formData.GrossWeight ? `${formData.GrossWeight} kg` : '—'}</span></div>
                <div className="info-cell"><label>Volume (CBM)</label><span>{formData.Volume || '—'}</span></div>
                <div className="info-cell"><label>Freight</label><span>{formData.Freight || '—'}</span></div>
            </div>

            <div className="section-title">Goods Declaration</div>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Marks & Nos</th>
                        <th>Description of Goods</th>
                        <th className="right">Packages</th>
                        <th className="right">Weight (kg)</th>
                        <th className="right">FOB Value (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {cargo.length === 0
                        ? <tr><td colSpan="6" className="center" style={{ color: '#999', padding: 16 }}>No cargo details</td></tr>
                        : cargo.map((r, i) => {
                            const invRow = inv[i] || {};
                            return (
                                <tr key={i}>
                                    <td className="center">{i + 1}</td>
                                    <td>{r.Mark || '—'}</td>
                                    <td>{r.Description || '—'}</td>
                                    <td className="right">{r.Packages || 0}</td>
                                    <td className="right">{fmt2(r.GrossWeight)}</td>
                                    <td className="right">{fmt2(invRow.SalesHome)}</td>
                                </tr>
                            );
                        })}
                    <tr className="total-row">
                        <td colSpan="3" className="right">TOTAL</td>
                        <td className="right">{totalPkgs}</td>
                        <td className="right">{fmt2(totalWeight)}</td>
                        <td className="right">₹ {fmt2(totalFOB)}</td>
                    </tr>
                </tbody>
            </table>

            {containers.length > 0 && <>
                <div className="section-title">Container Details</div>
                <table>
                    <thead>
                        <tr><th>#</th><th>Container No</th><th>Seal No</th><th>Description</th></tr>
                    </thead>
                    <tbody>
                        {containers.map((c, i) => (
                            <tr key={i}><td className="center">{i + 1}</td><td>{c.ContainerNo || '—'}</td><td>{c.SealNo || '—'}</td><td>{c.Description || '—'}</td></tr>
                        ))}
                    </tbody>
                </table>
            </>}

            <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12 }}>
                <strong>Declaration:</strong> I/We hereby declare that the information provided above is true and correct to the best of my/our knowledge, and that the goods described above are exported in accordance with applicable laws and regulations.
            </div>

            {formData.ShippingInstruction && <><div className="section-title">Shipping Instructions</div><p>{formData.ShippingInstruction}</p></>}

            <div className="sig-row">
                <div className="sig-box">Exporter's Signature &amp; Seal</div>
                <div className="sig-box">Customs Agent</div>
                <div className="sig-box">Customs Officer / Date</div>
            </div>
            <div className="watermark">Export Declaration — {formData.JobCode}</div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   DOCUMENT REGISTRY
══════════════════════════════════════════════════════════════════ */
const DOCS = [
    { id: 'invoice',      label: 'Invoice',                   icon: FileText, Component: InvoiceDoc,          color: '#667eea' },
    { id: 'tcn',          label: 'Truck Consignment Note',    icon: Truck,    Component: TruckConsignmentDoc, color: '#059669' },
    { id: 'arrival',      label: 'Arrival Notice',            icon: Anchor,   Component: ArrivalNoteDoc,      color: '#d97706' },
    { id: 'customs',      label: 'Customs Entry',             icon: Package,  Component: CustomsEntryDoc,     color: '#7c3aed' },
    { id: 'export_decl',  label: 'Export Declaration',        icon: FileCheck,Component: ExportDeclarationDoc,color: '#dc2626' },
];

/* ══════════════════════════════════════════════════════════════════
   MAIN: PrintDocuments  (drop into JobForm where you render tabs)
   Props:  formData, masterData
   Usage in JobForm:
     import PrintDocuments from './PrintDocuments';
     ...
     <PrintDocuments formData={formData} masterData={masterData} />
══════════════════════════════════════════════════════════════════ */
const PrintDocuments = ({ formData, masterData, companyName = 'Your Company Name' }) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);
    const printRef = useRef(null);

    /* Inject print CSS once */
    React.useEffect(() => {
        if (!document.getElementById('__print_css__')) {
            const style = document.createElement('style');
            style.id = '__print_css__';
            style.textContent = PRINT_CSS;
            document.head.appendChild(style);
        }
    }, []);

    const handlePrint = () => {
        const el = document.getElementById('print-area');
        if (!el) return;
        window.print();
    };

    const currentDoc = DOCS.find(d => d.id === active);

    return (
        <>
            {/* ── Trigger button — place this wherever suits in JobForm ── */}
            <button
                onClick={() => setOpen(true)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px', background: '#1e293b', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                    transition: 'background 0.2s',
                }}
                onMouseOver={e => e.currentTarget.style.background = '#334155'}
                onMouseOut={e => e.currentTarget.style.background = '#1e293b'}
            >
                <Printer size={16} /> Print Documents
            </button>

            {/* ── Modal overlay ── */}
            {open && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.55)', display: 'flex',
                    alignItems: 'flex-start', justifyContent: 'center',
                    overflowY: 'auto', padding: '24px 16px',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 14,
                        width: '100%', maxWidth: 980,
                        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
                        overflow: 'hidden',
                    }}>
                        {/* Modal header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '18px 24px',
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                            color: '#fff',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Printer size={20} />
                                <span style={{ fontWeight: 700, fontSize: 16 }}>Print Documents</span>
                                <span style={{
                                    background: 'rgba(255,255,255,0.15)', padding: '2px 10px',
                                    borderRadius: 20, fontSize: 12,
                                }}>{formData.JobCode}</span>
                            </div>
                            <button onClick={() => { setOpen(false); setActive(null); }}
                                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, borderRadius: 6 }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Document selector */}
                        <div style={{
                            display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb',
                            overflowX: 'auto', background: '#f8fafc',
                        }}>
                            {DOCS.map(doc => {
                                const Icon = doc.icon;
                                const isActive = active === doc.id;
                                return (
                                    <button key={doc.id} onClick={() => setActive(doc.id)}
                                        style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                                            padding: '14px 22px', border: 'none', cursor: 'pointer',
                                            background: isActive ? '#fff' : 'transparent',
                                            borderBottom: isActive ? `3px solid ${doc.color}` : '3px solid transparent',
                                            color: isActive ? doc.color : '#6b7280',
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: 12, whiteSpace: 'nowrap',
                                            transition: 'all 0.2s',
                                            minWidth: 130,
                                        }}>
                                        <Icon size={20} />
                                        {doc.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Document preview */}
                        {!active && (
                            <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
                                <Printer size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                                <p style={{ fontSize: 15, margin: 0 }}>Select a document type above to preview and print</p>
                            </div>
                        )}

                        {active && currentDoc && (
                            <>
                                {/* Print action bar */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px 24px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0',
                                }}>
                                    <span style={{ fontSize: 13, color: '#166534', fontWeight: 500 }}>
                                        Preview: <strong>{currentDoc.label}</strong>
                                    </span>
                                    <button onClick={handlePrint}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8,
                                            padding: '8px 20px',
                                            background: currentDoc.color,
                                            color: '#fff', border: 'none', borderRadius: 8,
                                            fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                        }}>
                                        <Printer size={16} /> Print / Save as PDF
                                    </button>
                                </div>

                                {/* Preview pane */}
                                <div style={{ maxHeight: '65vh', overflowY: 'auto', padding: 0, background: '#f1f5f9' }}>
                                    <div id="print-area" ref={printRef}
                                        style={{ background: '#fff', margin: '16px auto', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: 900 }}>
                                        <currentDoc.Component formData={formData} masterData={masterData} companyName={companyName} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default PrintDocuments;